import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * "Notify me when available" — a small, self-contained custom element.
 * Renders only when the currently selected variant is genuinely
 * unavailable (see snippets/back-in-stock-notify.liquid); posts to the
 * ancarta-brand-rep-backend Worker's isolated back-in-stock subsystem
 * (never Brand Rep signup/marketing — a separate route and separate
 * storage there too).
 *
 * Follows the same pattern as assets/product-inventory.js and
 * assets/local-pickup.js rather than touching product-form.js or
 * variant-picker.js: listens for the existing ProductSelectEvent (already
 * dispatched on every variant change) and morphs itself from the freshly-
 * fetched section HTML. Liquid re-evaluates whether the form should show
 * for the new variant on the server, so this component never has to
 * duplicate that availability logic client-side — it just stays in sync.
 *
 * No <form> element here on purpose (see the snippet's own comment): this
 * renders inside the real Add to Cart <form>, and HTML silently drops a
 * nested <form>'s own tag. Submission is a plain button click + Enter
 * key on the email input, handled entirely by this component.
 */
class BackInStockNotify extends Component {
  /** @type {Element | null} */
  #closestSection = null;

  connectedCallback() {
    super.connectedCallback();
    this.#closestSection = this.closest('.shopify-section, dialog');
    this.#closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#bindSubmitHandlers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #bindSubmitHandlers() {
    this.refs.submitButton?.addEventListener('click', this.#handleSubmit);
    this.refs.emailInput?.addEventListener('keydown', this.#handleKeydown);
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#handleSubmit();
    }
  };

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const newElement = detail.html.querySelector('back-in-stock-notify');
        if (!newElement) return;
        // Full morph (not childrenOnly) — the variant id lives on this
        // element's own data attribute, not just its children, and must
        // update whenever the shopper switches variant.
        morph(this, newElement);
        // A fresh morph may have replaced the submit button/input entirely
        // (e.g. coming from the previous variant's success/error state) —
        // rebind handlers on whatever nodes exist now.
        this.#bindSubmitHandlers();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[back-in-stock-notify] Event promise rejected:', error);
      });
  };

  #handleSubmit = async () => {
    const { formContainer, emailInput, submitButton, errorMessage } = this.refs;
    if (!emailInput || !submitButton) return;

    const email = emailInput.value.trim();
    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = '';
    }
    if (!email) return;

    const endpoint = this.dataset.apiEndpoint;
    if (!endpoint) return; // feature not configured — the form shouldn't render at all in this case

    submitButton.disabled = true;
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          productHandle: this.dataset.productHandle,
          variantId: this.dataset.variantId,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        if (formContainer) formContainer.hidden = true;
        if (this.refs.successMessage) this.refs.successMessage.hidden = false;
      } else if (errorMessage) {
        errorMessage.textContent = this.dataset.errorGeneric || '';
        errorMessage.hidden = false;
      }
    } catch (_error) {
      if (errorMessage) {
        errorMessage.textContent = this.dataset.errorGeneric || '';
        errorMessage.hidden = false;
      }
    } finally {
      submitButton.disabled = false;
    }
  };
}

if (!customElements.get('back-in-stock-notify')) {
  customElements.define('back-in-stock-notify', BackInStockNotify);
}
